import { useEffect, useMemo, useState } from "react";
import { Send, CheckCircle2, DollarSign, AlertCircle, Calendar } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const PRESETS = [
  { key: "month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
  { key: "all", label: "All Time" },
  { key: "custom", label: "Custom" },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmt2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KPI = ({ label, value, hint, icon: Icon, tone, tid }) => (
  <div data-testid={tid} className="bg-white border border-slate-200 rounded-md p-5">
    <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-${tone}-50 text-${tone}-600 border border-${tone}-100`}>
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </div>
    <div className="mt-4 font-display text-3xl font-extrabold text-slate-900 tracking-tight">{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mt-1">{label}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function Revenue() {
  const [period, setPeriod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const params = { period };
    if (period === "custom" && from && to) {
      params.date_from = new Date(from).toISOString();
      params.date_to = new Date(to + "T23:59:59").toISOString();
    }
    const { data } = await api.get("/revenue", { params });
    setData(data);
  };
  useEffect(() => { if (period !== "custom" || (from && to)) load(); /* eslint-disable-next-line */ }, [period, from, to]);

  const label = useMemo(() => PRESETS.find((p) => p.key === period)?.label || "All Time", [period]);

  return (
    <div data-testid="revenue-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Revenue</h1>
          <p className="text-sm text-slate-500 mt-1">Quotes sent, accepted, and dollars collected — pick any time window.</p>
        </div>
        <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-md p-1" data-testid="period-switch">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${period === p.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-md p-4 flex flex-wrap gap-3 items-end" data-testid="custom-range">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">From</div>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="date-from" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">To</div>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="date-to" />
          </div>
          <div className="text-xs text-slate-500 pb-2">Pick a start and end date to see the exact window.</div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI tid="rev-sent" label={`Quotes Sent (${label})`} value={fmt(data?.quotes_sent.amount)} hint={`${data?.quotes_sent.count ?? 0} quotes`} icon={Send} tone="blue" />
        <KPI tid="rev-accepted" label="Accepted" value={fmt(data?.quotes_accepted.amount)} hint={`${data?.quotes_accepted.count ?? 0} quotes`} icon={CheckCircle2} tone="emerald" />
        <KPI tid="rev-paid" label="Paid Revenue" value={fmt(data?.paid.amount)} hint={`${data?.paid.count ?? 0} invoices`} icon={DollarSign} tone="emerald" />
        <KPI tid="rev-out" label="Outstanding (All)" value={fmt(data?.outstanding.amount)} hint={`${data?.outstanding.count ?? 0} invoices`} icon={AlertCircle} tone="amber" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Breakdown</div>
            <div className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> {label}</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 340 }}>
          <ResponsiveContainer>
            <BarChart data={data?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} formatter={(v) => fmt2(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sent" name="Quotes Sent $" fill="var(--qq-accent)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Paid $" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
